// /casino-metrics — casino economy metrics dashboard (staff-only)
// Reference implementation for the economy-observability dashboard pattern
// (epic #550, issue #560). Sibling dashboards (#559, #561–#566) should reuse
// EconomyDashboardLayout from components/admin/economy-dashboard-layout.tsx.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { EconomyDashboardLayout } from '@/components/admin/economy-dashboard-layout';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PerGameRow {
  game: string;
  currency: string;
  rounds: number;
  wins: number;
  losses: number;
  pushes: number;
  handle: number;
  paid_out: number;
  house_take: number;
  hold_pct: number;
}

interface TotalsRow {
  currency: string;
  rounds: number;
  wins: number;
  losses: number;
  pushes: number;
  handle: number;
  paid_out: number;
  house_take: number;
  hold_pct: number;
}

interface MetricsResponse {
  ok: boolean;
  range: { since: number | null; until: number | null };
  per_game: PerGameRow[];
  totals: TotalsRow[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format an integer money value with thousands separators and the correct
 *  currency symbol per D-Currency-1 (cash = ¥ U+00A5, credits = ₢ U+20A2). */
function formatMoney(value: number, currency: string): string {
  const symbol = currency === 'credits' ? '₢' : '¥';
  return `${symbol}${value.toLocaleString()}`;
}

function buildQueryString(since: string, until: string, userId: string): string {
  const params = new URLSearchParams();
  if (since.trim()) params.set('since', since.trim());
  if (until.trim()) params.set('until', until.trim());
  if (userId.trim()) params.set('userId', userId.trim());
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MetricsTable({
  rows,
  showGame,
}: {
  rows: (PerGameRow | TotalsRow)[];
  showGame: boolean;
}) {
  return (
    <div className="overflow-x-auto rounded-sm border border-border">
      <table className="w-full text-sm" data-testid={showGame ? 'casino-per-game-table' : 'casino-totals-table'}>
        <thead className="bg-muted text-left">
          <tr>
            {showGame && (
              <th className="px-3 py-2 font-medium text-ink-mid">Game</th>
            )}
            <th className="px-3 py-2 font-medium text-ink-mid">Currency</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Rounds</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Wins</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Losses</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Pushes</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Handle</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Paid out</th>
            <th className="px-3 py-2 font-medium text-ink-mid">House take</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Hold %</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-divider">
          {rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-panel-elev">
              {showGame && (
                <td className="px-3 py-2 font-mono text-xs">
                  {'game' in row ? row.game : ''}
                </td>
              )}
              <td className="px-3 py-2 text-ink-low">{row.currency}</td>
              <td className="px-3 py-2 tabular-nums">{row.rounds.toLocaleString()}</td>
              <td className="px-3 py-2 tabular-nums">{row.wins.toLocaleString()}</td>
              <td className="px-3 py-2 tabular-nums">{row.losses.toLocaleString()}</td>
              <td className="px-3 py-2 tabular-nums">{row.pushes.toLocaleString()}</td>
              <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(row.handle, row.currency)}</td>
              <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(row.paid_out, row.currency)}</td>
              <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(row.house_take, row.currency)}</td>
              <td className="px-3 py-2 tabular-nums">{row.hold_pct.toFixed(2)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CasinoMetricsPage() {
  // Filter form state (draft; applied on "Apply" click)
  const [draftSince, setDraftSince] = useState('');
  const [draftUntil, setDraftUntil] = useState('');
  const [draftUserId, setDraftUserId] = useState('');

  // Applied filters (trigger query)
  const [appliedSince, setAppliedSince] = useState('');
  const [appliedUntil, setAppliedUntil] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');

  const { data, isLoading, isError } = useQuery<MetricsResponse>({
    queryKey: ['casino-metrics', appliedSince, appliedUntil, appliedUserId],
    queryFn: async () => {
      const qs = buildQueryString(appliedSince, appliedUntil, appliedUserId);
      const res = await apiFetch<MetricsResponse>(`/admin/casino-metrics${qs}`);
      if (!res.ok) throw new Error('Failed to load casino metrics.');
      return res.data;
    },
  });

  function handleApply() {
    setAppliedSince(draftSince);
    setAppliedUntil(draftUntil);
    setAppliedUserId(draftUserId);
  }

  return (
    <EconomyDashboardLayout
      title="Casino Metrics"
      description="Economy summary across all casino games. Defaults to all-time; narrow by Unix timestamp range."
      since={draftSince}
      until={draftUntil}
      onSinceChange={setDraftSince}
      onUntilChange={setDraftUntil}
      userId={draftUserId}
      onUserIdChange={setDraftUserId}
      onApply={handleApply}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Failed to load casino metrics."
    >
      {data != null && (
        <>
          {data.totals.length > 0 ? (
            <section className="space-y-2">
              <h2 className="text-lg font-medium text-ink-high">Totals</h2>
              <MetricsTable rows={data.totals} showGame={false} />
            </section>
          ) : (
            <p className="text-sm text-ink-low">No totals data available.</p>
          )}

          {data.per_game.length > 0 ? (
            <section className="space-y-2">
              <h2 className="text-lg font-medium text-ink-high">Per game</h2>
              <MetricsTable rows={data.per_game} showGame={true} />
            </section>
          ) : (
            <p className="text-sm text-ink-low">No per-game data available.</p>
          )}
        </>
      )}
    </EconomyDashboardLayout>
  );
}
